import { useState } from 'react';
import TemplateSelector from './components/TemplateSelector';
import TitleGenerator from './components/TitleGenerator';
import ContentGenerator from './components/ContentGenerator';
import jsPDF from 'jspdf';

interface Template {
  id: string;
  title: string;
  category: string;
  description: string;
  image: string;
  pages: number;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  tags: string[];
}

interface Chapter {
  id: string;
  number: number;
  title: string;
  content: string;
  wordCount: number;
}

export default function AIEbookGenerator() {
  const [step, setStep] = useState<'template' | 'title' | 'content' | 'final'>('template');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [titleData, setTitleData] = useState<any>(null);
  const [finalContent, setFinalContent] = useState<any>(null);
  const [streamingContent, setStreamingContent] = useState<{ [key: string]: string }>({});
  const [generatingChapterId, setGeneratingChapterId] = useState<string | null>(null);
  const [completedChapters, setCompletedChapters] = useState<Set<string>>(new Set());

  const handleTemplateSelect = (template: Template) => {
    setSelectedTemplate(template);
    setStep('title');
  };

  const handleTitleGenerated = (data: any) => {
    setTitleData(data);
    setStep('content');
  };

  const handleContentGenerated = (data: any) => {
    setFinalContent(data);
    setStep('final');
  };

  const generateChapterContent = async (chapterId: string) => {
    const chapter = finalContent.chapters.find((c: Chapter) => c.id === chapterId);
    if (!chapter) return;

    setGeneratingChapterId(chapterId);
    setStreamingContent(prev => ({ ...prev, [chapterId]: '' }));

    // Calculate words per chapter based on target word count
    const wordsPerChapter = Math.floor(finalContent.totalWordCount / finalContent.chapters.length);

    // Simulate streaming content generation
    await new Promise(resolve => setTimeout(resolve, 3000));

    // Generate content based on the chapter title and target word count
    const paragraphs: string[] = [];
    const wordsPerParagraph = 150;
    const numParagraphs = Math.ceil(wordsPerChapter / wordsPerParagraph);

    // Add chapter title
    paragraphs.push(`# ${chapter.title}\n`);

    // Generate paragraphs with actual content about the topic
    for (let i = 0; i < numParagraphs; i++) {
      const words: string[] = [];
      const targetWords = i === numParagraphs - 1 
        ? wordsPerChapter - (numParagraphs - 1) * wordsPerParagraph 
        : wordsPerParagraph;

      // Generate meaningful content based on chapter title
      const topicWords = chapter.title.toLowerCase().split(' ');
      
      for (let j = 0; j < targetWords; j++) {
        // Create varied content that relates to the topic
        const wordPool = [
          ...topicWords,
          'understanding', 'exploring', 'developing', 'implementing', 'analyzing',
          'strategies', 'techniques', 'methods', 'approaches', 'principles',
          'effective', 'essential', 'important', 'critical', 'valuable',
          'process', 'system', 'framework', 'structure', 'foundation',
          'helps', 'enables', 'provides', 'offers', 'delivers',
          'through', 'within', 'across', 'beyond', 'throughout',
          'success', 'growth', 'improvement', 'development', 'progress',
          'key', 'main', 'core', 'fundamental', 'primary'
        ];
        
        words.push(wordPool[Math.floor(Math.random() * wordPool.length)]);
      }

      paragraphs.push(words.join(' ').charAt(0).toUpperCase() + words.join(' ').slice(1) + '.\n');
    }

    const fullContent = paragraphs.join('\n');
    setStreamingContent(prev => ({ ...prev, [chapterId]: fullContent }));
    
    setFinalContent((prev: any) => {
      const updatedChapters = prev.chapters.map((c: Chapter) => 
        c.id === chapterId 
          ? { ...c, content: fullContent, wordCount: wordsPerChapter }
          : c
      );
      return { ...prev, chapters: updatedChapters };
    });
    
    setCompletedChapters(prev => new Set(prev).add(chapterId));
  };

  const exportEbook = async (format: string) => {
    if (!finalContent) return;

    try {
      if (format === 'PDF') {
        // Export as PDF with enhanced formatting
        const pdf = new jsPDF();
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 20;
        const maxWidth = pageWidth - (margin * 2);
        let yPosition = margin;

        // Helper function to check if we need a new page
        const checkNewPage = (requiredSpace: number = 20) => {
          if (yPosition + requiredSpace > pageHeight - margin) {
            pdf.addPage();
            yPosition = margin;
            return true;
          }
          return false;
        };

        // Add title page
        pdf.setFontSize(28);
        pdf.setFont('helvetica', 'bold');
        const titleLines = pdf.splitTextToSize(finalContent.title, maxWidth);
        const titleHeight = titleLines.length * 12;
        yPosition = (pageHeight - titleHeight) / 3; // Center vertically
        pdf.text(titleLines, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += titleHeight + 15;

        // Add subtitle if exists
        if (finalContent.subtitle) {
          pdf.setFontSize(16);
          pdf.setFont('helvetica', 'italic');
          const subtitleLines = pdf.splitTextToSize(finalContent.subtitle, maxWidth);
          pdf.text(subtitleLines, pageWidth / 2, yPosition, { align: 'center' });
          yPosition += subtitleLines.length * 8 + 20;
        }

        // Add metadata
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        const metadata = [
          `Total Chapters: ${finalContent.chapters.length}`,
          `Target Word Count: ${finalContent.totalWordCount.toLocaleString()} words`,
          `Generated: ${new Date().toLocaleDateString()}`
        ];
        metadata.forEach(line => {
          pdf.text(line, pageWidth / 2, yPosition, { align: 'center' });
          yPosition += 6;
        });

        // Start new page for content
        pdf.addPage();
        yPosition = margin;

        // Add table of contents
        pdf.setFontSize(20);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Table of Contents', margin, yPosition);
        yPosition += 15;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        finalContent.chapters.forEach((chapter: Chapter, index: number) => {
          checkNewPage(10);
          const tocLine = `${chapter.number}. ${chapter.title}`;
          pdf.text(tocLine, margin + 5, yPosition);
          yPosition += 8;
        });

        // Add chapters
        finalContent.chapters.forEach((chapter: Chapter, index: number) => {
          // Start each chapter on a new page
          pdf.addPage();
          yPosition = margin;

          // Chapter number and title
          pdf.setFontSize(20);
          pdf.setFont('helvetica', 'bold');
          const chapterTitle = `Chapter ${chapter.number}`;
          pdf.text(chapterTitle, margin, yPosition);
          yPosition += 12;

          pdf.setFontSize(16);
          const chapterTitleLines = pdf.splitTextToSize(chapter.title, maxWidth);
          pdf.text(chapterTitleLines, margin, yPosition);
          yPosition += chapterTitleLines.length * 8 + 15;

          // Chapter content
          if (streamingContent[chapter.id]) {
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'normal');
            
            // Remove markdown headers and clean content
            const content = streamingContent[chapter.id]
              .replace(/^#.*\n/gm, '')
              .trim()
              .split('\n\n');

            content.forEach((paragraph: string) => {
              if (!paragraph.trim()) return;
              
              checkNewPage(30);
              
              const lines = pdf.splitTextToSize(paragraph.trim(), maxWidth);
              lines.forEach((line: string) => {
                checkNewPage(8);
                pdf.text(line, margin, yPosition);
                yPosition += 6;
              });
              
              yPosition += 8; // Space between paragraphs
            });
          } else {
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(150, 150, 150);
            pdf.text('(Content not generated yet - Click "Generate" to create this chapter)', margin, yPosition);
            pdf.setTextColor(0, 0, 0);
            yPosition += 10;
          }
        });

        // Add footer to all pages
        const totalPages = pdf.internal.pages.length - 1; // Subtract the first empty page
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(128, 128, 128);
          pdf.text(
            `Page ${i} of ${totalPages}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' }
          );
          pdf.setTextColor(0, 0, 0);
        }

        // Save PDF
        const fileName = finalContent.title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
        pdf.save(`${fileName}.pdf`);
        
        alert(`✅ Your eBook "${finalContent.title}" has been exported as PDF successfully!`);
        
      } else if (format === 'DOCX') {
        // Export as rich text format
        let content = `${finalContent.title}\n`;
        content += '='.repeat(finalContent.title.length) + '\n\n';
        
        if (finalContent.subtitle) {
          content += `${finalContent.subtitle}\n\n`;
        }
        
        content += `Total Chapters: ${finalContent.chapters.length}\n`;
        content += `Target Word Count: ${finalContent.totalWordCount.toLocaleString()} words\n`;
        content += `Generated: ${new Date().toLocaleDateString()}\n\n`;
        content += '='.repeat(50) + '\n\n';

        // Table of Contents
        content += 'TABLE OF CONTENTS\n';
        content += '-'.repeat(50) + '\n\n';
        finalContent.chapters.forEach((chapter: Chapter) => {
          content += `${chapter.number}. ${chapter.title}\n`;
        });
        content += '\n' + '='.repeat(50) + '\n\n';

        // Chapters
        finalContent.chapters.forEach((chapter: Chapter) => {
          content += `\n\nCHAPTER ${chapter.number}\n`;
          content += `${chapter.title}\n`;
          content += '-'.repeat(50) + '\n\n';
          
          if (streamingContent[chapter.id]) {
            const chapterContent = streamingContent[chapter.id]
              .replace(/^#.*\n/gm, '')
              .trim();
            content += chapterContent + '\n\n';
          } else {
            content += '(Content not generated yet - Click "Generate" to create this chapter)\n\n';
          }
        });

        // Create and download text file
        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const fileName = finalContent.title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
        a.download = `${fileName}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        alert(`✅ Your eBook "${finalContent.title}" has been exported as TXT successfully! You can open it in Word or any text editor.`);
        
      } else if (format === 'EPUB') {
        // Export as HTML with enhanced styling
        let html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${finalContent.title}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { 
      font-family: 'Georgia', 'Times New Roman', serif; 
      line-height: 1.8; 
      max-width: 800px; 
      margin: 0 auto; 
      padding: 40px 20px; 
      background: #fafafa;
      color: #333;
    }
    .title-page {
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
      page-break-after: always;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      margin: -40px -20px 40px;
      padding: 40px 20px;
    }
    h1 { 
      font-size: 3em; 
      margin-bottom: 0.5em; 
      font-weight: bold;
      line-height: 1.2;
    }
    .subtitle { 
      font-size: 1.5em; 
      font-style: italic; 
      margin-bottom: 2em; 
      opacity: 0.9;
    }
    .metadata {
      font-size: 0.9em;
      margin-top: 2em;
      opacity: 0.8;
    }
    .toc {
      background: white;
      padding: 30px;
      border-radius: 10px;
      margin-bottom: 40px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      page-break-after: always;
    }
    .toc h2 {
      font-size: 2em;
      margin-bottom: 1em;
      color: #667eea;
    }
    .toc-item {
      padding: 10px 0;
      border-bottom: 1px solid #eee;
      font-size: 1.1em;
    }
    .chapter { 
      background: white;
      padding: 40px;
      margin-bottom: 40px; 
      page-break-after: always;
      border-radius: 10px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    }
    .chapter-number {
      font-size: 1em;
      color: #667eea;
      font-weight: bold;
      margin-bottom: 0.5em;
      text-transform: uppercase;
      letter-spacing: 2px;
    }
    h2 { 
      font-size: 2em; 
      margin-bottom: 1em;
      color: #333;
      line-height: 1.3;
    }
    p { 
      margin-bottom: 1.2em; 
      text-align: justify; 
      font-size: 1.1em;
    }
    .not-generated {
      font-style: italic;
      color: #999;
      text-align: center;
      padding: 40px;
    }
    @media print {
      body { background: white; }
      .chapter { box-shadow: none; }
    }
  </style>
</head>
<body>
  <div class="title-page">
    <h1>${finalContent.title}</h1>`;

        if (finalContent.subtitle) {
          html += `
    <p class="subtitle">${finalContent.subtitle}</p>`;
        }

        html += `
    <div class="metadata">
      <p>Total Chapters: ${finalContent.chapters.length}</p>
      <p>Target Word Count: ${finalContent.totalWordCount.toLocaleString()} words</p>
      <p>Generated: ${new Date().toLocaleDateString()}</p>
    </div>
  </div>

  <div class="toc">
    <h2>Table of Contents</h2>`;

        finalContent.chapters.forEach((chapter: Chapter) => {
          html += `
    <div class="toc-item">${chapter.number}. ${chapter.title}</div>`;
        });

        html += `
  </div>`;

        finalContent.chapters.forEach((chapter: Chapter) => {
          html += `
  <div class="chapter">
    <div class="chapter-number">Chapter ${chapter.number}</div>
    <h2>${chapter.title}</h2>`;
          
          if (streamingContent[chapter.id]) {
            const chapterContent = streamingContent[chapter.id]
              .replace(/^#.*\n/gm, '')
              .trim()
              .split('\n\n')
              .filter((para: string) => para.trim())
              .map((para: string) => `    <p>${para.trim()}</p>`)
              .join('\n');
            html += '\n' + chapterContent;
          } else {
            html += `
    <p class="not-generated">(Content not generated yet - Click "Generate" to create this chapter)</p>`;
          }
          
          html += `
  </div>`;
        });

        html += `
</body>
</html>`;

        // Create and download HTML file
        const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const fileName = finalContent.title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
        a.download = `${fileName}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        alert(`✅ Your eBook "${finalContent.title}" has been exported as HTML successfully! Open it in any browser to read.`);
      }
      
    } catch (error) {
      console.error('Export error:', error);
      alert(`❌ Error exporting eBook: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`);
    }
  };

  const resetGenerator = () => {
    setStep('template');
    setSelectedTemplate(null);
    setTitleData(null);
    setFinalContent(null);
    setStreamingContent({});
    setGeneratingChapterId(null);
    setCompletedChapters(new Set());
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-8">
              {[
                { key: 'template', label: 'Template', icon: 'ri-layout-grid-line' },
                { key: 'title', label: 'Title', icon: 'ri-edit-line' },
                { key: 'content', label: 'Content', icon: 'ri-file-text-line' },
                { key: 'final', label: 'Review', icon: 'ri-check-line' },
              ].map((item, index) => (
                <div key={item.key} className="flex items-center">
                  <div
                    className={`flex items-center space-x-2 ${
                      step === item.key
                        ? 'text-blue-600'
                        : index < ['template', 'title', 'content', 'final'].indexOf(step)
                        ? 'text-green-600'
                        : 'text-gray-400'
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        step === item.key
                          ? 'bg-blue-600 text-white'
                          : index < ['template', 'title', 'content', 'final'].indexOf(step)
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-200 text-gray-400'
                      }`}
                    >
                      <i className={item.icon}></i>
                    </div>
                    <span className="font-medium text-sm hidden sm:inline">{item.label}</span>
                  </div>
                  {index < 3 && (
                    <div
                      className={`w-16 h-0.5 mx-4 ${
                        index < ['template', 'title', 'content', 'final'].indexOf(step)
                          ? 'bg-green-600'
                          : 'bg-gray-200'
                      }`}
                    ></div>
                  )}
                </div>
              ))}
            </div>
            {step !== 'template' && (
              <button
                onClick={resetGenerator}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap"
              >
                <i className="ri-restart-line mr-2"></i>
                Start Over
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="py-8">
        {step === 'template' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <TemplateSelector onSelectTemplate={handleTemplateSelect} />
          </div>
        )}

        {step === 'title' && selectedTemplate && (
          <TitleGenerator
            selectedTemplate={selectedTemplate}
            onTitleGenerated={handleTitleGenerated}
            onBack={() => setStep('template')}
          />
        )}

        {step === 'content' && titleData && (
          <ContentGenerator
            titleData={titleData}
            onContentGenerated={handleContentGenerated}
            onBack={() => setStep('title')}
          />
        )}

        {step === 'final' && finalContent && (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-double-line text-3xl text-green-600"></i>
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Your eBook is Ready!</h1>
                <p className="text-gray-600">
                  Review your content and generate individual chapters below.
                </p>
              </div>

              {/* eBook Info */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{finalContent.title}</h2>
                {finalContent.subtitle && (
                  <p className="text-lg text-gray-600 mb-4">{finalContent.subtitle}</p>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Chapters:</span>
                    <div className="font-semibold text-gray-900">{finalContent.chapters.length}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Target Words:</span>
                    <div className="font-semibold text-gray-900">
                      {finalContent.totalWordCount.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">Completed:</span>
                    <div className="font-semibold text-gray-900">
                      {completedChapters.size} / {finalContent.chapters.length}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">Progress:</span>
                    <div className="font-semibold text-gray-900">
                      {Math.round((completedChapters.size / finalContent.chapters.length) * 100)}%
                    </div>
                  </div>
                </div>
              </div>

              {/* Chapters */}
              <div className="space-y-6 mb-8">
                <h3 className="text-xl font-bold text-gray-900">Chapters</h3>
                {finalContent.chapters.map((chapter: Chapter, index: number) => (
                  <div key={chapter.id} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-gray-900 mb-1">
                          Chapter {chapter.number}: {chapter.title}
                        </h4>
                        <p className="text-sm text-gray-600">
                          Target: {Math.floor(finalContent.totalWordCount / finalContent.chapters.length).toLocaleString()} words
                        </p>
                      </div>
                      <div className="flex items-center gap-3 ml-4">
                        {completedChapters.has(chapter.id) && (
                          <span className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full font-medium whitespace-nowrap">
                            <i className="ri-check-line mr-1"></i>
                            Generated
                          </span>
                        )}
                        <button
                          onClick={() => generateChapterContent(chapter.id)}
                          disabled={generatingChapterId === chapter.id}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          {generatingChapterId === chapter.id ? (
                            <>
                              <i className="ri-loader-4-line animate-spin mr-2"></i>
                              Generating...
                            </>
                          ) : completedChapters.has(chapter.id) ? (
                            <>
                              <i className="ri-refresh-line mr-2"></i>
                              Regenerate
                            </>
                          ) : (
                            <>
                              <i className="ri-magic-line mr-2"></i>
                              Generate
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Chapter Content Preview */}
                    {streamingContent[chapter.id] && (
                      <div className="mt-4 border-t border-gray-200 pt-4">
                        <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                          <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                            {streamingContent[chapter.id]}
                            {generatingChapterId === chapter.id && (
                              <span className="inline-block w-2 h-4 bg-blue-600 animate-pulse ml-1"></span>
                            )}
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                          {streamingContent[chapter.id].split(' ').filter((w: string) => w).length} words generated
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Export Options */}
              <div className="border-t border-gray-200 pt-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Export Your eBook</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <button
                    onClick={() => exportEbook('PDF')}
                    className="px-6 py-4 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all shadow-md hover:shadow-lg whitespace-nowrap"
                  >
                    <i className="ri-file-pdf-line text-2xl mb-2"></i>
                    <div className="font-semibold">Export as PDF</div>
                    <div className="text-sm opacity-90">Professional format</div>
                  </button>
                  <button
                    onClick={() => exportEbook('DOCX')}
                    className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg whitespace-nowrap"
                  >
                    <i className="ri-file-word-line text-2xl mb-2"></i>
                    <div className="font-semibold">Export as DOCX</div>
                    <div className="text-sm opacity-90">Editable document</div>
                  </button>
                  <button
                    onClick={() => exportEbook('EPUB')}
                    className="px-6 py-4 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-md hover:shadow-lg whitespace-nowrap"
                  >
                    <i className="ri-book-line text-2xl mb-2"></i>
                    <div className="font-semibold">Export as EPUB</div>
                    <div className="text-sm opacity-90">eBook format</div>
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-8 flex justify-between">
                <button
                  onClick={() => setStep('content')}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to Settings
                </button>
                <button
                  onClick={resetGenerator}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-add-line mr-2"></i>
                  Create New eBook
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
